<template>
  <v-data-table
    v-model="selectedRows"
    :headers="formattedHeaders"
    :items="formattedItems"
    :item-key="primaryKey"
    class="elevation-1"
    calculate-widths
    :items-per-page="itemsPerPage"
    :footer-props="{
      'items-per-page-options': [5, 10, 20, 30, 40, 50, -1],
    }"
    :show-select="selectable"
    dense
    :loading="loading"
    :loading-text="loadingText"
    :sort-by="sortBy"
    :sort-desc="sortDescending"
    @input="handleSelectedRowsChanged"
  >
    <template #top>
      <slot name="toolbar" />
    </template>
    <template #body="{ items }">
      <tbody>
        <tr v-for="item in items" :key="item[primaryKey]" @click="handleRowClick(item)">
          <td v-if="selectable">
            <v-checkbox
              v-model="selectedRows"
              :value="item"
              class="selectable-table-cell"
              hide-details
              @click.prevent.stop
            />
          </td>
          <td v-for="(header, index) in formattedHeaders" :key="index" :class="alignClass(header.align)">
            <v-btn
              v-if="header.type === 'button'"
              :disabled="header.disableOn(header.value, item)"
              class="table-cell-button"
              @click.stop.prevent="handleTableCellClick(header.value, item)"
            >
              {{ formatButtonField(header, item) }}
            </v-btn>
            <a
              v-else-if="header.type === 'link'"
              class="table-cell-link"
              @click.stop.prevent="handleTableCellClick(header.text, item)"
            >
              {{ item[header.value] }}
            </a>
            <component :is="header.component" v-else-if="header.component" v-bind="header" :item="item" />
            <span v-else>
              {{ formatField(header, item) }}
            </span>
          </td>
        </tr>
      </tbody>
    </template>
  </v-data-table>
</template>

<script>
import {
  MeasurementUnit,
  flattenHeaderValues,
  formatMeasurementHeaders,
  formatMeasurementFields,
  formatCustomFields,
  orderImperialDistances,
} from '@/utils';

export default {
  name: 'DataTable',
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
    primaryKey: {
      type: String,
      required: true,
    },
    unit: {
      type: String,
      default: null,
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingText: {
      type: String,
      default: null,
    },

    sortBy: {
      type: Array,
      default: () => [],
    },

    sortDescending: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedRows: [],
    };
  },
  computed: {
    formattedHeaders() {
      return this.headers
        .filter((header) => header.type !== 'hidden') // hidden headers
        .map(flattenHeaderValues)
        .map(formatMeasurementHeaders(this.unit)) // add units to headers
        .map((header) => ({
          disableOn: () => false, // default to not disabled
          ...header,
          sortable: header.type !== 'button',
          sort: (a, b) => {
            if (this.unit === MeasurementUnit.IMPERIAL && header.type === 'distance') {
              return orderImperialDistances(a, b);
            }
            if (['distance', 'area', 'percentage'].includes(header.type)) {
              return +a < +b ? -1 : 1;
            }
            return a < b ? -1 : 1;
          },
        }));
    },
    formattedItems() {
      return this.data
        .map(formatMeasurementFields(this.headers, this.unit)) // apply unit conversions
        .map(formatCustomFields(this.headers));
    },
  },
  watch: {
    formattedItems() {
      this.resetSelectedRows();
    },
  },
  methods: {
    alignClass(align) {
      switch (align) {
        case 'right':
          return 'text-right';
        case 'center':
          return 'center';
        default:
          return 'text-start';
      }
    },
    formatField(field, item) {
      return typeof field.formatter === 'function' ? field.formatter(item[field.value]) : item[field.value];
    },
    formatButtonField(field, item) {
      return typeof field.formatter === 'function' ? field.formatter(item, field) : field.text;
    },
    resetSelectedRows() {
      this.selectedRows = [];
      this.$emit('onSelectedRowsChanged', []);
    },
    handleRowClick(row) {
      this.$emit('onRowClick', row);
    },
    handleTableCellClick(column, row) {
      this.$emit('onTableCellClick', { column, row });
    },
    handleSelectedRowsChanged(selectedRows) {
      this.$emit('onSelectedRowsChanged', selectedRows);
    },
  },
};
</script>

<style scoped>
.table-cell-button {
  height: 28px !important;
}
.table-cell-link {
  padding-right: 1em;
  text-decoration: none;
}
.selectable-table-cell {
  margin: 0px;
  padding: 0px;
}
</style>
